import React, { useEffect, useState } from "react";
import { api, fmtUSD } from "../lib/api";
import { Sparkles, AlertTriangle, ShieldAlert, Info } from "lucide-react";

const SEV = {
  high: { cls: "bg-[#fee2e2] text-[#b91c1c] border-[#fecaca]", Icon: ShieldAlert },
  medium: { cls: "bg-[#fef3c7] text-[#92400e] border-[#fde68a]", Icon: AlertTriangle },
  low: { cls: "bg-[#e0f2fe] text-[#0369a1] border-[#bae6fd]", Icon: Info },
};

export default function Anomalies() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.exec.anomalies().then(setData).catch(console.error);
  }, []);

  if (!data) {
    return (
      <div className="px-6 lg:px-10 py-8">
        <div className="cs-card p-10 text-center text-muted-foreground" data-testid="anomaly-loading">Scanning for anomalies…</div>
      </div>
    );
  }

  return (
    <div className="px-6 lg:px-10 py-8 space-y-5" data-testid="anomaly-page">
      <div className="cs-card p-5 border-l-4" style={{ borderLeftColor: "#be185d" }} data-testid="anomaly-ai-summary">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#be185d] shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">CloudScore AI · Anomaly Summary</div>
            <p className="text-sm leading-relaxed">{data.ai_summary}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="cs-card px-4 py-2.5" data-testid="anomaly-count">
          <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Active Anomalies</div>
          <div className="cs-mono text-lg font-semibold">{data.summary.count}</div>
        </div>
        <div className="cs-card px-4 py-2.5" data-testid="anomaly-impact">
          <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Monthly Impact</div>
          <div className="cs-mono text-lg font-semibold text-[#be185d]">{fmtUSD(data.summary.total_monthly_impact, { compact: true })}</div>
        </div>
        {["high", "medium", "low"].map((s) => (
          <span key={s} className={`px-2.5 py-1 rounded-full text-xs border font-medium ${SEV[s].cls}`} data-testid={`anomaly-sev-${s}`}>
            {data.summary[s]} {s}
          </span>
        ))}
      </div>

      <div className="space-y-3">
        {data.anomalies.map((a) => {
          const S = SEV[a.severity];
          return (
            <div key={a.id} className="cs-card p-5" data-testid={`anomaly-${a.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide border font-semibold inline-flex items-center gap-1 ${S.cls}`}>
                  <S.Icon className="w-3 h-3" /> {a.severity}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{a.service} <span className="text-muted-foreground font-normal">· {a.account} · {a.provider}</span></div>
                  <div className="cs-mono text-[11px] text-muted-foreground mt-0.5">
                    baseline {fmtUSD(a.baseline, { compact: true })} → current {fmtUSD(a.current, { compact: true })}
                    <span className="text-[#be185d] font-semibold ml-2">+{fmtUSD(a.delta, { compact: true })} ({a.delta_pct > 0 ? "+" : ""}{a.delta_pct}%)</span>
                  </div>
                </div>
                <div className="ml-auto text-right shrink-0">
                  <div className="cs-mono text-[11px] text-muted-foreground">confidence {(a.confidence * 100).toFixed(0)}%</div>
                  <div className="cs-mono text-[11px] text-muted-foreground">~{a.started_days_ago}d duration</div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mt-2.5">{a.explanation}</p>
              <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
                {Object.entries(a.dimensions).filter(([, v]) => v && v !== "—").map(([k, v]) => (
                  <span key={k} className="px-2 py-0.5 rounded-sm bg-[hsl(var(--cs-tint))] border border-border text-[10px] cs-mono">
                    {k.replace("_", " ")}: <span className="font-semibold">{v}</span>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
        {!data.anomalies.length && <div className="cs-card p-10 text-center text-sm text-muted-foreground">No anomalies detected.</div>}
      </div>
    </div>
  );
}
